import { useState, useEffect } from 'react'
import { motion } from 'framer-motion'
import { Layers, Users, Building2, Star, Zap, Heart, Loader2, Lock } from 'lucide-react'
import CategoryCard from '../components/ui/CategoryCard'
import { useAuth } from '../context/AuthContext'
import AuthModal from '../components/ui/AuthModal'

export default function BrowsePage() {
  const { isAuthenticated, loading: authLoading } = useAuth()
  const [showAuthModal, setShowAuthModal] = useState(false)
  const [categories, setCategories] = useState([])
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    if (!authLoading) {
      if (!isAuthenticated) {
        setShowAuthModal(true)
      } else {
        fetchCategories()
      }
    }
  }, [isAuthenticated, authLoading])

  const fetchCategories = async () => {
    setLoading(true)
    try {
      const response = await fetch('/api/cloudinary/logos')
      if (!response.ok) throw new Error('Failed to fetch')
      
      const data = await response.json()
      const logos = data.logos || []
      
      // Group logos by category and count
      const categoryMap = {}
      logos.forEach(logo => {
        const cat = logo.category || 'Other'
        if (!categoryMap[cat]) {
          categoryMap[cat] = {
            name: cat,
            slug: cat.toLowerCase().replace(/\s+/g, '-'),
            count: 0,
            description: `${cat} logos`,
            icon: getCategoryIcon(cat),
            gradient: getCategoryGradient(cat)
          }
        }
        categoryMap[cat].count++
      })
      
      setCategories(Object.values(categoryMap))
    } catch (error) {
      console.error('Error fetching categories:', error)
    } finally {
      setLoading(false)
    }
  }

  const getCategoryIcon = (category) => {
    const iconMap = {
      'Technical Clubs': <Zap className="text-white" size={28} />,
      'Cultural Clubs': <Heart className="text-white" size={28} />,
      'Social Clubs': <Users className="text-white" size={28} />,
      'Special': <Star className="text-white" size={28} />,
      'Institute': <Building2 className="text-white" size={28} />,
      'Departments': <Layers className="text-white" size={28} />
    }
    return iconMap[category] || <Layers className="text-white" size={28} />
  }

  const getCategoryGradient = (category) => {
    const gradientMap = {
      'Technical Clubs': 'bg-gradient-blue',
      'Cultural Clubs': 'bg-gradient-pink',
      'Social Clubs': 'bg-gradient-orange',
      'Special': 'bg-gradient-green',
      'Institute': 'bg-gradient-purple',
      'Departments': 'bg-gradient-teal'
    }
    return gradientMap[category] || 'bg-gradient-blue'
  }

  // Show loading while checking auth
  if (authLoading) {
    return (
      <main className="min-h-screen bg-nitrr-background dark:bg-gray-950 flex items-center justify-center">
        <div className="text-center">
          <Loader2 className="w-12 h-12 animate-spin text-nitrr-primary mx-auto mb-4" />
          <p className="text-gray-600 dark:text-gray-400">Loading...</p>
        </div>
      </main>
    )
  }

  // Show auth required if not authenticated
  if (!isAuthenticated) {
    return (
      <>
        <AuthModal
          isOpen={showAuthModal}
          onClose={() => window.location.href = '/'}
          defaultMode="signin"
        />
        <main className="min-h-screen bg-gradient-to-br from-blue-50 via-purple-50 to-pink-50 dark:from-gray-900 dark:via-blue-900 dark:to-purple-900 flex items-center justify-center p-4">
          <motion.div
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            className="max-w-md w-full bg-white dark:bg-gray-800 rounded-2xl shadow-2xl p-8 text-center"
          >
            <div className="inline-flex items-center justify-center w-20 h-20 bg-gradient-to-br from-blue-500 to-purple-600 rounded-full mb-6 shadow-lg">
              <Lock className="w-10 h-10 text-white" />
            </div>
            <h1 className="text-3xl font-bold text-gray-900 dark:text-white mb-4">
              Authentication Required
            </h1>
            <p className="text-gray-600 dark:text-gray-400 mb-8">
              Please sign in to browse our logo categories and access premium downloads.
            </p>
            <button
              onClick={() => setShowAuthModal(true)}
              className="w-full py-3 px-6 bg-gradient-to-r from-blue-600 to-purple-600 text-white font-semibold rounded-lg hover:from-blue-700 hover:to-purple-700 transition-all shadow-lg hover:shadow-xl transform hover:scale-105"
            >
              Sign In to Continue
            </button>
          </motion.div>
        </main>
      </>
    )
  }

  return (
    <main className="min-h-screen bg-nitrr-background dark:bg-gray-950">
      <div className="container mx-auto px-4 py-12 max-w-7xl">
        <motion.div
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          className="text-center mb-16"
        >
          <motion.h1
            className="text-5xl md:text-6xl font-bold text-nitrr-text dark:text-white mb-6"
            initial={{ scale: 0.5 }}
            animate={{ scale: 1 }}
            transition={{ type: "spring", stiffness: 100 }}
          >
            Browse <span className="text-gradient-primary">Categories</span>
          </motion.h1>
          <p className="text-xl text-nitrr-text-secondary dark:text-gray-300 max-w-2xl mx-auto">
            Explore our comprehensive collection of logos organized by category
          </p>
        </motion.div>

        {loading ? (
          <div className="flex items-center justify-center py-20">
            <Loader2 className="w-12 h-12 animate-spin text-nitrr-primary" />
          </div>
        ) : categories.length === 0 ? (
          <div className="text-center py-12">
            <p className="text-nitrr-text-secondary dark:text-gray-400">
              No categories available yet. Upload logos to create categories.
            </p>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {categories.map((category, index) => (
              <motion.div
                key={category.slug}
                initial={{ opacity: 0, y: 50 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: index * 0.1 }}
              >
                <CategoryCard {...category} />
              </motion.div>
            ))}
          </div>
        )}
      </div>
    </main>
  )
}
